import type { updateEntity } from '@unifyapps/network/generated/entity-rest-api/entity-rest-api';
import { useUpdateEntity } from '@unifyapps/network/generated/entity-rest-api/entity-rest-api';
import type { Entity } from '@unifyapps/network/generated/models/entity';
import useMutationWithVersionError from '@unifyapps/network/hooks/useMutationWithVersionError';
import type { UseMutateAsyncFunction, UseMutateFunction } from '@unifyapps/network/react-query';
import type { ErrorType } from '@unifyapps/network/utils/fetch';
import { useCallback } from 'react';

type UpdateEntityData = Awaited<ReturnType<typeof updateEntity>>;
type UpdateEntityError = ErrorType<unknown>;
type UpdateEntityVariables = { data: Entity };

function useUpdateEntityWithVersionError() {
  const { mutate, mutateAsync, ...rest } = useMutationWithVersionError(useUpdateEntity());

  const mutateWithRetry: UseMutateFunction<
    UpdateEntityData,
    UpdateEntityError,
    UpdateEntityVariables
  > = useCallback(
    (variables, options) => {
      mutate(variables, {
        ...options,
        getRetryVariables: (error, errorVariables) => {
          return {
            data: {
              ...errorVariables.data,
              version: error.payload?.currentVersion || errorVariables.data.version,
            },
          };
        },
      });
    },
    [mutate],
  );

  const mutateAsyncWithRetry: UseMutateAsyncFunction<
    UpdateEntityData,
    UpdateEntityError,
    UpdateEntityVariables
  > = useCallback(
    (variables: UpdateEntityVariables, options) => {
      return mutateAsync(variables, {
        ...options,
        getRetryVariables: (error, errorVariables) => {
          return {
            data: {
              ...errorVariables.data,
              version: error.payload?.currentVersion || errorVariables.data.version,
            },
          };
        },
      });
    },
    [mutateAsync],
  );

  return { mutate: mutateWithRetry, mutateAsync: mutateAsyncWithRetry, ...rest };
}

export default useUpdateEntityWithVersionError;
